/** Nombre con el que el equipo reconoce al cliente: alias propio del CRM o, si no hay, el nombre de Zoho. */
export function nombreVisible(cliente) {
  if (!cliente) return "";
  return (cliente.alias || "").trim() || (cliente.nombre || "").trim() || (cliente.razon_social || "").trim() || "Sin nombre";
}

/** Referencia legal que se muestra en gris debajo del nombre visible (solo si aporta algo distinto). */
export function nombreLegal(cliente) {
  if (!cliente) return "";
  const legal = (cliente.razon_social || cliente.nombre || "").trim();
  return legal && legal !== nombreVisible(cliente) ? legal : "";
}

export function coincideBusqueda(cliente, q) {
  const needle = (q || "").trim().toLowerCase();
  if (!needle) return true;
  return [cliente.alias, cliente.nombre, cliente.razon_social, cliente.nif, cliente.ciudad, cliente.email]
    .some((v) => (v || "").toLowerCase().includes(needle));
}

export const TRABAJO_ESTADOS = [
  { key: "por_empezar", label: "Por empezar", dot: "bg-slate-400", chip: "bg-slate-100 text-slate-700 border-slate-200", col: "bg-slate-50" },
  { key: "en_proceso", label: "En proceso", dot: "bg-blue-500", chip: "bg-blue-50 text-blue-700 border-blue-200", col: "bg-blue-50/50" },
  { key: "en_revision", label: "En revisión", dot: "bg-amber-500", chip: "bg-amber-50 text-amber-800 border-amber-200", col: "bg-amber-50/50" },
  { key: "hecho", label: "Hecho", dot: "bg-emerald-500", chip: "bg-emerald-50 text-emerald-700 border-emerald-200", col: "bg-emerald-50/50" },
];

export const TRABAJO_ESTADO = Object.fromEntries(TRABAJO_ESTADOS.map((e) => [e.key, e]));

export function formatFechaCorta(isoDate) {
  if (!isoDate) return "";
  const d = new Date(isoDate.length === 10 ? `${isoDate}T12:00:00` : isoDate);
  return d.toLocaleDateString("es-ES", { day: "numeric", month: "short" }).replace(".", "");
}

export function fechaVencida(isoDate) {
  if (!isoDate) return false;
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  return new Date(`${isoDate}T23:59:59`) < hoy;
}

export function formatHora(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
}

/** "14:32" si es hoy, "ayer", o "3 nov" para la lista de chats. */
export function formatActividad(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const hoy = new Date();
  if (d.toDateString() === hoy.toDateString()) return formatHora(iso);
  const ayer = new Date(hoy);
  ayer.setDate(hoy.getDate() - 1);
  if (d.toDateString() === ayer.toDateString()) return "ayer";
  return d.toLocaleDateString("es-ES", { day: "numeric", month: "short" }).replace(".", "");
}

export function formatDia(iso) {
  const d = new Date(iso);
  const hoy = new Date();
  if (d.toDateString() === hoy.toDateString()) return "Hoy";
  const ayer = new Date(hoy);
  ayer.setDate(hoy.getDate() - 1);
  if (d.toDateString() === ayer.toDateString()) return "Ayer";
  return d.toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" });
}

export function formatTamano(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Texto con los datos fiscales para copiar en bloque. */
export function datosFiscalesTexto(c) {
  const linea2 = [c.codigo_postal, c.ciudad].filter(Boolean).join(" ");
  return [
    c.razon_social || c.nombre,
    c.nif && `NIF/CIF: ${c.nif}`,
    c.direccion,
    linea2,
    c.provincia,
    c.pais,
    c.telefono && `Tel.: ${c.telefono}`,
    c.email,
    c.web,
  ].filter(Boolean).join("\n");
}

const ORDEN_ESTADO_BARRA = { en_proceso: 0, en_revision: 1, por_empezar: 2, hecho: 3 };

/**
 * Orden de los trabajos dentro de cada estado:
 * - Pendientes: primero los que tienen fecha límite (la más cercana o ya vencida arriba); después los que no
 *   tienen fecha, del más antiguo al más nuevo (lo que más tiempo lleva esperando, primero).
 * - Hechos: el terminado más recientemente arriba.
 * Con `porEstado` (barra del chat) se agrupan además por estado: en proceso → en revisión → por empezar → hecho.
 */
export function ordenarTrabajos(lista, { porEstado = false } = {}) {
  return [...lista].sort((a, b) => {
    if (porEstado && a.estado !== b.estado) return ORDEN_ESTADO_BARRA[a.estado] - ORDEN_ESTADO_BARRA[b.estado];
    if (a.estado === "hecho" && b.estado === "hecho") return (b.fecha_completado || "").localeCompare(a.fecha_completado || "");
    if (a.fecha_limite && b.fecha_limite) return a.fecha_limite.localeCompare(b.fecha_limite);
    if (a.fecha_limite) return -1;
    if (b.fecha_limite) return 1;
    return (a.fecha_creacion || "").localeCompare(b.fecha_creacion || "");
  });
}

/** Extensión en mayúsculas para mostrar en la tarjeta de un archivo ("PDF", "ZIP"…). */
export function extensionArchivo(nombre) {
  const m = (nombre || "").match(/\.([a-z0-9]{1,5})$/i);
  return m ? m[1].toUpperCase() : "ARCHIVO";
}

export function hostDeUrl(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}
